import styled from '@benzinga/themetron';
import { Button, Icon } from '@benzinga/core-ui';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { faArrowRight, faBookOpen } from '@fortawesome/pro-regular-svg-icons';

export interface BasicInfoCardsProps {
  cards: {
    caption: string;
    cta_btn_link?: string;
    cta_title?: string;
    subtitle: string;
    title: string;
  }[];
  card_layout: string;
}

export const BasicInfoCards = ({ card_layout = '', cards }: BasicInfoCardsProps) => {
  const isCta = card_layout === 'cta';

  return (
    Array.isArray(cards) &&
    cards.map((item, i) => (
      <BasicInfoCardsWrapper className={`item-wrapper ${card_layout}`} key={i}>
        {isCta && (
          <div aria-hidden className="item-icon">
            <Icon icon={faBookOpen} />
          </div>
        )}
        <div className="item-content">
          {item?.caption && <p className="item-caption">{item.caption}</p>}
          <h5 className="item-title">{item.title}</h5>
          <p className="item-description" dangerouslySetInnerHTML={{ __html: sanitizeHTML(item?.subtitle ?? '') }} />
        </div>
        {isCta && (
          <div className="action-btn">
            <Button as="a" href={item.cta_btn_link} size="md" variant="flat-blue">
              {item.cta_title}
              <Icon className="action-arrow" icon={faArrowRight} />
            </Button>
          </div>
        )}
      </BasicInfoCardsWrapper>
    ))
  );
};

const BasicInfoCardsWrapper = styled.div`
  &.item-wrapper {
    flex: 1;
    padding: 24px;
    background: #ffffff;
    border: 1px solid rgba(25, 41, 64, 0.1);
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(32, 50, 71, 0.06);
    display: flex;
    flex-direction: column;

    .item-content {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
    }

    .item-caption {
      font-weight: 700;
      font-size: 16px;
      line-height: 24px;
      color: #5b7292;
    }

    .item-title {
      margin-top: 8px;
      margin-bottom: 12px;
      font-weight: 700;
      font-size: 22px;
      line-height: 1.3;
      letter-spacing: -0.01em;
      color: #192940;
    }

    .item-description {
      margin-top: auto;
      font-size: 15px;
      line-height: 1.5;
      color: #3d4f66;

      a {
        color: #3f83f8;
        font-weight: 600;
        text-decoration: underline;
        text-underline-offset: 2px;
      }
    }

    .action-btn {
      margin-top: 20px;
    }

    &.cta {
      flex-direction: row;
      align-items: center;
      gap: 20px;
      padding: 22px 24px;
      background: #f5f9ff;
      border: 1px solid #d6e4f7;
      border-radius: 12px;
      box-shadow: none;

      .item-icon {
        flex-shrink: 0;
        width: 48px;
        height: 48px;
        border-radius: 10px;
        background: #ffffff;
        border: 1px solid #d6e4f7;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #3f83f8;

        svg {
          width: 20px;
          height: 20px;
        }
      }

      .item-content {
        flex: 1;
        min-width: 0;
      }

      .item-title {
        margin: 0 0 4px;
        font-size: 15px;
        line-height: 1.3;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #192940;
      }

      .item-description {
        margin: 0;
        font-size: 14px;
        line-height: 1.45;
        color: #5b7292;
        max-width: 52em;

        a {
          color: #3f83f8;
        }
      }

      .action-btn {
        margin: 0;
        flex-shrink: 0;

        a {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
        }

        .action-arrow {
          width: 14px;
          height: 14px;
        }
      }

      @media screen and (max-width: 767px) {
        flex-wrap: wrap;
        gap: 14px;
        padding: 18px;

        .item-icon {
          width: 40px;
          height: 40px;
        }

        .item-content {
          flex: 1 1 calc(100% - 60px);
        }

        .action-btn {
          flex: 1 1 100%;

          a {
            width: 100%;
            justify-content: center;
          }
        }
      }
    }
  }
`;
